import React, { useRef } from 'react'
import styled from 'styled-components'
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside'
import useToggle from '../../../../hooks/useToggle'

import { ExternalLink } from '../../../../theme'
import { LinkKeys, LINKS, ROUTES } from '../../../../constants/links'
import { Title, UniIcon } from '../../../Header'
import Logo from '../../../../assets/logo-white.svg'
import WordmarkDark from '../../../../assets/wordmark_white.svg'
import { Link, useLocation } from 'react-router-dom'

interface SidenavProps {
  open: boolean
}

const StyledMenuButton = styled.button`
  width: 100%;
  height: 100%;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  height: 2.1875rem;

  padding: 2.4px 8px;
  border-radius: 8px;

  :hover,
  :focus {
    cursor: pointer;
    outline: none;
    background-color: rgb(30, 30, 30);
  }

  svg {
    margin-top: 0.125rem;
  }
`

const StyledMenu = styled.div`
  margin-left: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  text-align: left;
`

const MenuItem = styled(ExternalLink)`
  display: flex;
  align-items: center;
  flex: 1;
  padding: 8px 8px;
  color: ${({ theme }) => theme.text1};

  :hover {
    color: ${({ theme }) => theme.text2};

    cursor: pointer;
    text-decoration: none;
  }
  > svg {
    margin-right: 0.5rem;
  }
`

const RouteLink = styled(Link)<{ active: string }>`
  display: flex;
  align-items: center;
  flex: 1;
  padding: 8px 8px;
  font-size: 16px !important;
  font-weight: 500;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  text-decoration: ${(props) => (props.active === 'true' ? 'underline' : 'none')};
  :hover {
    color: ${({ theme }) => theme.text1};
  }

  color: ${(props) => (props.active === 'true' ? '#4AFC38' : '')};
  text-decoration-color: ${(props) => (props.active === 'true' ? '#4AFC38' : '')};
  text-underline-offset: 6px;
`

const AccordionButton = styled.button<{ open: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  font-size: 16px;
  padding: 8px;
  gap: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};
  :hover {
    color: ${({ theme }) => theme.text1};
  }
`

const SidenavContainer = styled.div<SidenavProps>`
  height: 100%;
  width: ${(props) => (props.open ? '20rem' : '0')};
  position: fixed;
  top: 0;
  right: 0;
  overflow-x: hidden;
  transition: 0.3s width;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  padding: ${(props) => (props.open ? '20px' : '0')};
  transition: all 300ms;
`

const SidenavContainerInside = styled.div`
  padding: 20px;
  background-color: ${({ theme }) => theme.bg0}
  height: 100%;
  border-radius: 0.75rem;
  overflow: auto;
`

const CloseButton = styled.span`
  position: absolute;
  top: 30px;
  right: 36px;
  font-size: 1.5rem;
  margin-left: 3.125rem;
  color: white;
  cursor: pointer;
`

const Overlay = styled.div<SidenavProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${(props) => (props.open ? 'block' : 'none')};
`

export default function MenuV2() {
  const node = useRef<HTMLDivElement>()
  const [open, toggle] = useToggle(false)
  const location = useLocation()
  const { pathname } = location

  useOnClickOutside(node, open ? toggle : undefined)

  const [openAccordions, setOpenAccordions] = React.useState<string[]>([])

  const toggleAccordion = (name: string) => {
    setOpenAccordions((prevState) =>
      prevState.includes(name) ? prevState.filter((item) => item !== name) : [...prevState, name]
    )
  }

  return (
    <>
      <Overlay open={open} onClick={toggle} />
      <SidenavContainer open={open} ref={node as any}>
        <SidenavContainerInside>
          {open && (
            <>
              <Title href=".">
                <UniIcon>
                  <img style={{ width: '24px', marginRight: '6px' }} src={Logo} alt="logo" />
                </UniIcon>
                <img style={{ marginLeft: '.25rem', width: '112px' }} src={WordmarkDark} alt="logo" />
              </Title>
              <div
                style={{
                  width: '100%',
                  borderBottom: '.0625rem solid #282828',
                  height: 0,
                  marginTop: '16px',
                  marginBottom: '16px',
                }}
              ></div>
              <CloseButton onClick={toggle}>&times;</CloseButton>
              {ROUTES.map((route) => {
                return (
                  <div key={route.name}>
                    {'childrens' in route ? (
                      <div>
                        <AccordionButton
                          onClick={() => toggleAccordion(route.name)}
                          open={openAccordions.includes(route.name)}
                        >
                          {route.name}
                          {openAccordions.includes(route.name) ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M6 15l6 -6l6 6" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                          )}
                        </AccordionButton>
                        {openAccordions.includes(route.name) && (
                          <div style={{ paddingLeft: '12px' }}>
                            {route.childrens.map((child, childIndex) => (
                              <RouteLink
                                key={childIndex}
                                to={child.route}
                                active={(
                                  child.routeMatches.findIndex((route) => new RegExp(route).test(pathname)) !== -1
                                ).toString()}
                              >
                                {child.name}
                              </RouteLink>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <RouteLink
                        to={route.route}
                        active={(
                          route.routeMatches.findIndex((routeM) => new RegExp(routeM).test(pathname)) !== -1
                        ).toString()}
                      >
                        {route.name}
                      </RouteLink>
                    )}
                  </div>
                )
              })}
              <div
                style={{
                  width: '100%',
                  borderBottom: '.0625rem solid #282828',
                  height: 0,
                  marginTop: '16px',
                  marginBottom: '16px',
                }}
              ></div>
              {Object.keys(LINKS).map((link) => {
                const linkKey = link as LinkKeys
                return (
                  <MenuItem id="link" href={LINKS[linkKey].link} key={linkKey}>
                    {LINKS[linkKey].logo} {LINKS[linkKey].name}
                  </MenuItem>
                )
              })}
            </>
          )}
          <div
            style={{
              width: '100%',
              borderBottom: '.0625rem solid #282828',
              height: 0,
              marginTop: '16px',
              marginBottom: '16px',
            }}
          ></div>
        </SidenavContainerInside>
      </SidenavContainer>

      <StyledMenu>
        <StyledMenuButton onClick={toggle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgb(255, 255, 255)"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-menu-2"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 6l16 0" />
            <path d="M4 12l16 0" />
            <path d="M4 18l16 0" />
          </svg>
        </StyledMenuButton>
      </StyledMenu>
    </>
  )
}
